/**
 * Local hex primitives so the UI can build the board without importing
 * @bobt/engine's node:fs-touching modules. Axial pointy-top; matches the
 * engine's math exactly (verified in tests).
 */

import type { Hex } from "./types.js";

export const NEIGHBOR_DIRS: readonly Hex[] = [
  { q: 1, r: 0 },
  { q: 1, r: -1 },
  { q: 0, r: -1 },
  { q: -1, r: 0 },
  { q: -1, r: 1 },
  { q: 0, r: 1 },
];

export function hexKey(h: Hex): string {
  return `${h.q},${h.r}`;
}

export function parseHexKey(k: string): Hex {
  const [q, r] = k.split(",");
  return { q: Number(q), r: Number(r) };
}

export function hexEq(a: Hex, b: Hex): boolean {
  return a.q === b.q && a.r === b.r;
}

export function hexDistance(a: Hex, b: Hex): number {
  const dq = a.q - b.q;
  const dr = a.r - b.r;
  return Math.max(Math.abs(dq), Math.abs(dr), Math.abs(dq + dr));
}

export function neighbors(h: Hex): Hex[] {
  return NEIGHBOR_DIRS.map((d) => ({ q: h.q + d.q, r: h.r + d.r }));
}

function rotate60Cw(h: Hex): Hex {
  return { q: -h.r, r: h.q + h.r };
}

export function rotate60(h: Hex, steps: number): Hex {
  const n = ((steps % 6) + 6) % 6;
  let cur = h;
  for (let i = 0; i < n; i++) cur = rotate60Cw(cur);
  return cur;
}

/** Pointy-top axial → pixel. `size` = hex radius. */
export function axialToPixel(h: Hex, size: number): { x: number; y: number } {
  return {
    x: size * Math.sqrt(3) * (h.q + h.r / 2),
    y: size * 1.5 * h.r,
  };
}

/** Vertices of a pointy-top hex at cx,cy with radius `size`. */
export function hexPolygonPoints(
  cx: number,
  cy: number,
  size: number,
): string {
  const pts: string[] = [];
  for (let i = 0; i < 6; i++) {
    const angle = ((60 * i - 30) * Math.PI) / 180;
    pts.push(`${cx + size * Math.cos(angle)},${cy + size * Math.sin(angle)}`);
  }
  return pts.join(" ");
}
